import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import Input from "../input";
import TextArea from "../textarea";
import Button from "../button";
import { Category } from "@app/types";
import { Form, Formik, FormikContextType } from "formik";
import { ChangeEvent, createRef, useEffect, useRef } from "react";
import { createCategoryValidationSchema } from "@app/validations";
import { FormControl } from "../form-control";
import { Images } from "@assets/images";
import { MAX_SIZE_UPLOAD, SystemMessage } from "@app/constants";
import Checkbox from "../checkbox";

interface CategoryModalProps {
  isEdit: boolean;

  category: Category;
}

function CategoryModal({
  portalDialogRef,
  portalData,
}: PortalDialogProps<CategoryModalProps>) {
  const formRef = createRef<
    FormikContextType<{
      title: string;
      name: string;
      description: string;
      filename?: string;
      order: number | null;
      isContainFree?: boolean;
      isContainPurchase?: boolean;
      isContainVip?: boolean;
    }>
  >();
  const inputFileRef = useRef<HTMLInputElement>(null);

  const onSubmit = (values: {
    title: string;
    name: string;
    description: string;
    order: number | null;
    isContainFree?: boolean;
    isContainPurchase?: boolean;
    isContainVip?: boolean;
  }) => {
    if (!formRef.current) return;

    if (!inputFileRef.current?.files?.length && !portalData?.isEdit) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);

      return;
    }

    const file = inputFileRef.current?.files?.[0] ?? null;

    if (file && file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);

      return;
    }

    const category = {
      name: values.name,
      title: values.title,
      description: values.description,
      icon: file,
      order: values.order,
      isContainFree: values.isContainFree,
      isContainPurchase: values.isContainPurchase,
      isContainVip: values.isContainVip,
    };

    portalDialogRef.close({ category });
  };

  useEffect(() => {
    if (portalData?.category && formRef.current) {
      formRef.current.setValues({
        title: portalData.category.title,
        name: portalData.category.name,
        description: portalData.category.description,
        filename: "",
        order: portalData.category.order,
        isContainFree: portalData.category.isContainFree ?? false,
        isContainPurchase: portalData.category.isContainPurchase ?? false,
        isContainVip: portalData.category.isContainVip ?? false,
      });
    }
  }, [portalData?.category]);

  const onAddFileClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRef.current) return;

    if (!event.target.files?.length) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    formRef.current.setFieldError("filename", undefined);
    formRef.current.setFieldValue("filename", file.name);
  };

  return (
    <Modal onCancel={portalDialogRef.close}>
      <div className="w-[423px] py-7 px-6">
        <div className="text-black font-semibold text-xl">
          {portalData?.isEdit ? "Chỉnh sửa" : "Thêm danh mục"}
        </div>
        <Formik
          initialValues={{
            title: "",
            name: "",
            description: "",
            filename: "",
            order: 0,
            isContainFree: false,
            isContainPurchase: false,
            isContainVip: false,
          }}
          onSubmit={onSubmit}
          innerRef={formRef}
          validationSchema={createCategoryValidationSchema}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <FormControl name="title">
              <Input
                inputClassName="text-[#2C2D33]"
                className="mt-7"
                placeholder="Tiêu đề danh mục"
              />
            </FormControl>
            <FormControl name="name">
              <Input
                inputClassName="text-[#2C2D33]"
                className="mt-2"
                placeholder="Tên danh mục"
              />
            </FormControl>
            <FormControl name="description">
              <TextArea
                textAreaClassName="bg-color pl-4 border-none outline-none text-[#2C2D33] rounded-lg"
                className="mt-2"
                placeholder="Mô tả"
                width="100%"
                height="auto"
                rows={4}
              ></TextArea>
            </FormControl>
            <FormControl name="filename">
              <Input
                className="mt-3"
                inputClassName="!pl-4 pr-[50px]"
                placeholder="File icon"
                disabled
                inputIcon={Images.LinkIcon.default}
                inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                onIconClick={onAddFileClick}
              />
            </FormControl>
            <input
              ref={inputFileRef}
              type="file"
              name="file"
              hidden
              onChange={handleFileChange}
            />
            <FormControl name="order">
              <Input
                inputClassName="text-[#2C2D33]"
                className="mt-2"
                placeholder="Order"
              />
            </FormControl>
            <label htmlFor="">
              <div className="mt-6 flex">
                <FormControl name="isContainFree">
                  <Checkbox
                    label="Hiển thị sách FREE"
                    labelClassName="text-sm text-[#83898C]"
                  />
                </FormControl>
              </div>
            </label>
            <label htmlFor="">
              <div className="mt-6 flex">
                <FormControl name="isContainPurchase">
                  <Checkbox
                    label="Hiển thị sách mua lẻ"
                    labelClassName="text-sm text-[#83898C]"
                  />
                </FormControl>
              </div>
            </label>
            <label htmlFor="">
              <div className="mt-6 flex">
                <FormControl name="isContainVip">
                  <Checkbox
                    label="Hiển thị sách VIP"
                    labelClassName="text-sm text-[#83898C]"
                  />
                </FormControl>
              </div>
            </label>
            <div className="mt-11 flex justify-center gap-4">
              <Button
                size="xs"
                label="Hủy"
                theme="secondary"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
                onClick={portalDialogRef.close}
              />
              <Button
                type="submit"
                size="xs"
                label={portalData?.isEdit ? "Lưu" : "Thêm"}
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
    </Modal>
  );
}

export default CategoryModal;
